import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabaseClient'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid
} from 'recharts'
import { useLanguage } from '../hooks/useLanguage'

export default function Analiticas() {
  const { t } = useLanguage()

  const [transactions, setTransactions] = useState([])
  const [loading, setLoading] = useState(true)

  async function getUser() {
    const { data } = await supabase.auth.getUser()
    return data.user
  }

  async function fetchTransactions() {

    setLoading(true)

    const user = await getUser()

    const startYear = new Date().getFullYear()

    const startDate = `${startYear}-01-01`
    const endDate = `${startYear}-12-31`

    const { data, error } = await supabase
      .from('transactions')
      .select('*')
      .eq('user_id', user.id)
      .gte('date', startDate)
      .lte('date', endDate)

    if (!error) {
      setTransactions(data)
    }

    setLoading(false)
  }

  useEffect(() => {
    fetchTransactions()
  }, [])

  // MESES (claves de traducción, en orden)
  const monthKeys = [
    'mes_ene', 'mes_feb', 'mes_mar', 'mes_abr',
    'mes_may', 'mes_jun', 'mes_jul', 'mes_ago',
    'mes_sep', 'mes_oct', 'mes_nov', 'mes_dic'
  ]

  // DATA PARA EL GRÁFICO
  const chartData = monthKeys.map((monthKey, index) => {

    const monthNumber = String(index + 1).padStart(2, '0')

    const ingresos = transactions
      .filter(
        t =>
          t.type === 'income' &&
          t.date.slice(5, 7) === monthNumber
      )
      .reduce((acc, t) => acc + t.amount, 0)

    const gastos = transactions
      .filter(
        t =>
          t.type === 'expense' &&
          t.date.slice(5, 7) === monthNumber
      )
      .reduce((acc, t) => acc + t.amount, 0)

    return {
      month: t(monthKey),
      ingresos,
      gastos
    }
  })

  // MEJOR MES
  const mejorMes = [...chartData].sort(
    (a, b) => b.ingresos - a.ingresos
  )[0]

  // PEOR MES
  const peorMes = [...chartData].sort(
    (a, b) => b.gastos - a.gastos
  )[0]

  // TOTALES
  const totalIngresos = chartData.reduce(
    (acc, m) => acc + m.ingresos,
    0
  )

  const totalGastos = chartData.reduce(
    (acc, m) => acc + m.gastos,
    0
  )

  const ahorro = totalIngresos - totalGastos

  // CONSEJO AUTOMÁTICO
  function getConsejo() {

    if (totalGastos > totalIngresos) {
      return t('consejo_gastos_superan')
    }

    if (ahorro > totalIngresos * 0.4) {
      return t('consejo_excelente')
    }

    if (ahorro > totalIngresos * 0.2) {
      return t('consejo_vas_bien')
    }

    return t('consejo_reducir_gastos')
  }

 return (
  <div style={{
    minHeight: '100vh',
    background: 'var(--color-bg)'
  }}>

    {/* HEADER */}
    <header style={{
      background: 'var(--color-surface)',
      borderBottom: '1px solid var(--color-border)',
      padding: '16px 20px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    }}>
      <div>
        <h1 style={{
          margin: 0,
          fontSize: 24,
          fontWeight: 700,
          fontFamily: 'var(--font-display)'
        }}>
          {t('analiticas_header')}
        </h1>

        <div style={{
          fontSize: 13,
          color: 'var(--color-muted)',
          marginTop: 4
        }}>
          {t('analiticas_subtitulo')}
        </div>
      </div>

      <button
        onClick={() => window.history.back()}
        style={{
          padding: '8px 14px',
          borderRadius: 10,
          border: '1px solid var(--color-border)',
          background: 'transparent',
          cursor: 'pointer',
          fontSize: 13,
          color: 'var(--color-text)'
        }}
      >
        {t('volver')}
      </button>
    </header>

    <main style={{
      maxWidth: 1200,
      margin: '0 auto',
      padding: 20
    }}>

      {loading ? (

        <div style={{
          background: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          borderRadius: 18,
          padding: 30,
          textAlign: 'center'
        }}>
          {t('analiticas_cargando')}
        </div>

      ) : (

        <>
          {/* CARDS */}
          <div style={{
            display: 'grid',
            gridTemplateColumns:
              window.innerWidth < 768
                ? 'repeat(2, 1fr)'
                : 'repeat(4, 1fr)',
            gap: 16,
            marginBottom: 24
          }}>

            {/* INGRESOS */}
            <div style={{
                background: 'var(--color-surface)',
                borderRadius: 20,
                padding: 20,
                border: '1px solid var(--color-border)'
            }}>
              <div style={{
                fontSize: 12,
                fontWeight: 700,
                color: '#166534',
                textTransform: 'uppercase'
              }}>
                {t('analiticas_ingresos')}
              </div>

              <div style={{
                marginTop: 10,
                fontSize: 28,
                fontWeight: 800,
                color: '#15803d'
              }}>
                $
                {totalIngresos.toLocaleString('es-CO')}
              </div>
            </div>

            {/* GASTOS */}
            <div style={{
                background: 'var(--color-surface)',
                borderRadius: 20,
                padding: 20,
                border: '1px solid var(--color-border)'
            }}>
              <div style={{
                fontSize: 12,
                fontWeight: 700,
                color: '#991b1b',
                textTransform: 'uppercase'
              }}>
                {t('analiticas_gastos')}
              </div>

              <div style={{
                marginTop: 10,
                fontSize: 28,
                fontWeight: 800,
                color: '#dc2626'
              }}>
                $
                {totalGastos.toLocaleString('es-CO')}
              </div>
            </div>

            {/* AHORRO */}
            <div style={{
                background: 'var(--color-surface)',
                borderRadius: 20,
                padding: 20,
                border: '1px solid var(--color-border)'
            }}>
              <div style={{
                fontSize: 12,
                fontWeight: 700,
                color: '#1d4ed8',
                textTransform: 'uppercase'
              }}>
                {t('analiticas_ahorro')}
              </div>

              <div style={{
                marginTop: 10,
                fontSize: 28,
                fontWeight: 800,
                color: '#2563eb'
              }}>
                $
                {ahorro.toLocaleString('es-CO')}
              </div>
            </div>

            {/* MEJOR MES */}
            <div style={{
                background: 'var(--color-surface)',
                borderRadius: 20,
                padding: 20,
                border: '1px solid var(--color-border)'
            }}>
              <div style={{
                fontSize: 12,
                fontWeight: 700,
                color: '#7e22ce',
                textTransform: 'uppercase'
              }}>
                {t('analiticas_mejor_mes')}
              </div>

              <div style={{
                marginTop: 10,
                fontSize: 28,
                fontWeight: 800,
                color: '#9333ea'
              }}>
                {mejorMes.month}
              </div>
            </div>

          </div>

          {/* GRÁFICA */}
          <div style={{
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: 22,
            padding: 24,
            marginBottom: 24
          }}>

            <h2 style={{
              marginTop: 0,
              marginBottom: 20,
              fontSize: 20
            }}>
              {t('analiticas_rendimiento_anual')}
            </h2>

            <div style={{
              width: '100%',
              height: 400
            }}>
              <ResponsiveContainer>
                <BarChart data={chartData}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#e5e7eb"
                  />

                  <XAxis dataKey="month" />

                  <YAxis />

                  <Tooltip
                    contentStyle={{
                      background: 'var(--color-surface)',
                      border: '1px solid var(--color-border)',
                      borderRadius: 12,
                      color: 'var(--color-text)'
                    }}
                    labelStyle={{ color: 'var(--color-muted)', marginBottom: 6 }}
                    itemStyle={{ color: 'var(--color-text)' }}
                  />

                  <Bar
                    dataKey="ingresos"
                    fill="#22c55e"
                    radius={[8, 8, 0, 0]}
                  />

                  <Bar
                    dataKey="gastos"
                    fill="#ef4444"
                    radius={[8, 8, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>

          </div>

{/* RECOMENDACIONES */}
<div style={{
  background: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 22,
  padding: 24
}}>
  <h2 style={{ marginTop: 0, marginBottom: 20, fontSize: 20 }}>
    {t('analiticas_recomendaciones')}
  </h2>

  <div style={{
    display: 'grid',
    gridTemplateColumns: window.innerWidth < 768 ? '1fr' : '1fr 1fr',
    gap: 14
  }}>

    <div style={{ background: 'var(--color-bg)', border: '1px solid var(--color-border)', padding: 18, borderRadius: 16, lineHeight: 1.6, color: 'var(--color-text)' }}>
      {getConsejo()}
    </div>

    <div style={{ background: 'var(--color-bg)', border: '1px solid var(--color-border)', padding: 18, borderRadius: 16, color: 'var(--color-text)' }}>
      {t('analiticas_mejor_mes_desc_pre')} <strong>{mejorMes.month}</strong>{t('analiticas_mejor_mes_desc_post')}
    </div>

    <div style={{ background: 'var(--color-bg)', border: '1px solid var(--color-border)', padding: 18, borderRadius: 16, color: 'var(--color-text)' }}>
      {t('analiticas_peor_mes_desc_pre')} <strong>{peorMes.month}</strong>.
    </div>

    <div style={{ background: 'var(--color-bg)', border: '1px solid var(--color-border)', padding: 18, borderRadius: 16, color: 'var(--color-text)' }}>
      {t('analiticas_ahorro_total_pre')} <strong>${ahorro.toLocaleString('es-CO')}</strong> {t('analiticas_ahorro_total_post')}
    </div>
</div>

  </div>

        </>
      )}
    </main>
  </div>
)
}